import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";

/** GFM enables pipe tables, strikethrough, task lists, etc. */
export const policyRemarkPlugins = [remarkGfm];

/** Shared ReactMarkdown mapping for venue policy / consent copy. */
export const policyMarkdownComponents: Components = {
  p: ({ children }) => (
    <p className="mb-2 last:mb-0 text-sm leading-relaxed text-gray-700">
      {children}
    </p>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="font-medium text-gray-800 not-italic">{children}</em>
  ),
  ul: ({ children }) => (
    <ul className="mb-2 list-disc space-y-1 pl-5 text-sm text-gray-700 last:mb-0">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-decimal space-y-1 pl-5 text-sm text-gray-700 last:mb-0">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="text-sm text-gray-700">{children}</li>,
  h1: ({ children }) => (
    <h1 className="mb-2 text-lg font-bold text-gray-900">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 text-base font-bold text-gray-900">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-2 text-sm font-bold text-gray-900">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="mb-2 text-sm font-semibold text-gray-800">{children}</h4>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-2 rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-gray-700 last:mb-0">
      {children}
    </blockquote>
  ),
  hr: () => (
    <hr className="mb-2 border-0 border-t border-gray-200 pb-2" />
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-blue-600 underline hover:text-blue-800"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code className="rounded bg-gray-100 px-1 py-0.5 text-xs text-gray-800">
      {children}
    </code>
  ),
  table: ({ children }) => (
    <div className="mb-2 last:mb-0 overflow-x-auto">
      <table className="min-w-full border-collapse text-left text-sm text-gray-700">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="border-b border-gray-200 bg-gray-50">{children}</thead>
  ),
  tbody: ({ children }) => <tbody className="divide-y divide-gray-200">{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children }) => (
    <th className="border border-gray-200 px-3 py-2 font-semibold text-gray-900">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-gray-200 px-3 py-2 align-top">{children}</td>
  ),
};